import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

const COMPANY_NAME = "NetBasis International";

const title = "Presencia empresarial";
const h1Text = "Su socio estratégico para una sólida presencia comercial local en la región DACH. Con una representación y dirección en Duesseldorf, posicionamos a su empresa de TIC de manera excelente, segura y rentable en el mercado.";
const heroImage = "https://net-basis.net/Bilder/duesseldorf-media-harbor-rhine-tower-business-location.webp";
const heroAlt = "El Medienhafen de Düsseldorf y la Torre del Rin evocan presencia local en el mercado DACH.";
const heroClaimLine1 = "Centro económico";
const heroClaimLine2 = "de Düsseldorf";
const benefitsTitle = "Cómo se beneficia de nuestra experiencia en __presencia empresarial__:";
const p1 = "Para muchas empresas, tanto la entrada en nuevos mercados como su exitoso desarrollo están asociados a un alto nivel de esfuerzo. Además, con frecuencia se subestiman las sensibilidades, los diferentes puntos de vista o las expectativas de los potenciales socios comerciales internacionales. Estos últimos suelen preferir contar con un interlocutor local que hable su idioma y conozca de cerca sus costumbres, el derecho de su país y sus prácticas comerciales. La falta de conocimiento del mercado, del país y del idioma, así como los recursos financieros o de personal insuficientes, representan obstáculos no insignificantes.";
const p2Text = "Precisamente aquí, NetBasis International ayuda con su servicio de \"presencia comercial\", actuando como un puente entre su empresa y los nuevos mercados, para superar eficazmente todas las dificultades u obstáculos potenciales.";
const p2Parts = p2Text.split(COMPANY_NAME);
const p3 = "Con una presencia comercial local y una dirección en Düsseldorf, que no solo mantiene el nombre de su empresa o le proporciona un número de teléfono y una persona de contacto local, sino que también gestiona la totalidad de sus actividades en su nombre, estará excelente y rentablemente posicionado.";
const bottomClaim = "NetBasis International - drive to excellence with excellent drivers !";

const benefits = [
  "Minimización rigurosa de costes, esfuerzo operativo, tiempo y riesgos",
  "Sin necesidad de contar con una presencia jurídica ni entidad propia sobre el terreno",
  "Oportunidades de entrada inmediata y flexible al mercado",
  "Ocasión favorable para realizar una prueba de mercado y de producto fundamentada",
  "Sin necesidad de personal propio ni infraestructura administrativa",
  "Seguimiento más eficiente de los contactos comerciales ya existentes",
  "Alivio sostenible para su propio departamento de exportación",
  "Eliminación total de las barreras lingüísticas mediante una comunicación directa y sencilla",
  "Acceso inmediato a bases de datos, listas de direcciones e integración en redes",
  "Entrada óptima o transición fluida hacia su propia sucursal local",
];

export default function PresenciaEmpresarialEsPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Presencia Empresarial | NetBasis International"
        description="Su sucursal local en el mercado DACH sin cargas administrativas: profesional, presente y lista para actuar de inmediato."
        ogUrl="https://net-basis.net/es/servicios/desarrollo-de-negocio/presencia-empresarial"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero image */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src={heroImage}
                alt={heroAlt}
                title={title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex items-start justify-start pt-[22%] md:pt-[10%] lg:pt-[12%] pl-[8%] md:pl-[10%] pointer-events-none">
                <p
                  className="text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-center leading-[1.15] md:leading-[1.5] animate-[trainingTextSlide_12s_ease-in-out_infinite]"
                  style={{
                    fontFamily: '"Open Sans", sans-serif',
                  }}
                >
                  <span className="block">{heroClaimLine1}</span>
                  {heroClaimLine2 && <span className="block">{heroClaimLine2}</span>}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {title}
            </h2>

            <h1 className="font-semibold text-foreground-950 text-sm md:text-base mb-4">
              {h1Text}
            </h1>

            <div className="space-y-4 text-foreground-950 leading-relaxed text-base">
              <p>
                {(() => {
                  const parts = p1.split(COMPANY_NAME);
                  return parts.map((part, i) => (
                    <span key={i}>
                      {part}
                      {i < parts.length - 1 && (
                        <strong className="text-primary-600">{COMPANY_NAME}</strong>
                      )}
                    </span>
                  ));
                })()}
              </p>
              <p>
                {p2Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p2Parts.length - 1 && (
                      <strong className="text-primary-600">{COMPANY_NAME}</strong>
                    )}
                  </span>
                ))}
              </p>
              <p>{p3}</p>
            </div>

            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mt-8 md:mt-10 mb-5 md:mb-7">
              {benefitsTitle.split("__").map((part, i) =>
                i % 2 === 1 ? <em key={i} className="mr-[0.15em]">{part}</em> : part
              )}
            </h2>

            <div className="space-y-1.5 md:space-y-2 text-foreground-950 leading-relaxed text-base">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-2 md:gap-3">
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-primary-600 text-base md:text-lg"></i>
                  </div>
                  <p>{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {bottomClaim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}